// The "attach the JD" control, shared by the template form and the job form.
//
// ─── WHY ONE COMPONENT AND NOT TWO FIELDS ───────────────────────────────────
//
// A template authors a JD; a job carries the one it will actually email. Those
// are two different columns for good reasons (see the migration), but the
// CONTROL is identical, and the fiddly parts — uploading before save, telling
// "no document" apart from "still uploading", not losing a URL when the user
// cancels the file picker — are the parts worth having in one place.
//
// ─── UPLOAD HAPPENS ON PICK, NOT ON SAVE ────────────────────────────────────
//
// The alternative is to hold the File in state and upload it inside the form's
// submit. That reads tidier and behaves worse: the save then has two ways to
// fail, and a 5 MB upload on a slow connection makes "Save" appear to hang with
// no progress and no way to tell whether it is the PDF or the job that is
// stuck. Uploading on pick puts the wait where the user caused it, and by the
// time they press Save the value is just a string.
//
// The cost is an unreferenced blob if they upload and then abandon the form.
// That is the cheaper failure — it confuses nobody, where a half-saved
// attachment would.
//
// ─── WHAT THE PARENT OWNS ───────────────────────────────────────────────────
//
// This component holds no committed state. `value` is `{ url, name }` or null,
// and every change is reported through `onChange`. The parent decides when it
// reaches the server, which is what lets Cancel actually cancel.

import { useRef, useState } from "react";
import { Box, Typography, Button, IconButton, Tooltip, CircularProgress, Alert } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { jdService } from "../services/jobService";

const TEAL = {
  50: "#e6f7f5",
  100: "#ccefeb",
  300: "#66cfc3",
  500: "#0c8a9b",
  600: "#0a7a8a",
  700: "#086a78",
};

const fontFam = "'Plus Jakarta Sans', 'Inter', sans-serif";

// Kept in step with the server's cap (jdDocumentController.MAX_BYTES) and with
// the mailer's. Checked here as well so a 20 MB file is refused instantly
// rather than after a minute of uploading — the server check is the real one,
// this one is only about not wasting the user's time.
const MAX_BYTES = 8 * 1024 * 1024;

const prettySize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const JdAttachmentField = ({
  // { url, name } or null.
  value,
  onChange,
  label = "Job Description (PDF)",
  // Shown under the control when there is no document yet. The two call sites
  // want to say different things about where the document ends up.
  helperText,
  // Optional note for a job that inherited its JD from a template, so the
  // recruiter can see why a document is already attached to a job they have
  // not touched.
  inheritedNote,
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const attached = Boolean(value?.url);

  const pick = () => {
    setError("");
    inputRef.current?.click();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    // Reset the input straight away, so choosing the SAME file again after a
    // failed upload still fires a change event. Without this, a retry of the
    // identical file looks like a dead button.
    event.target.value = "";
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (!isPdf) {
      setError(
        "Only PDFs can be attached — that is what the candidate receives, " +
          "and it is the one format that renders the same for all of them.",
      );
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(
        `That PDF is ${prettySize(file.size)}. The limit is ` +
          `${MAX_BYTES / 1024 / 1024} MB, because anything larger cannot be ` +
          `emailed to a candidate.`,
      );
      return;
    }

    setUploading(true);
    setError("");
    try {
      const res = await jdService.upload(file);
      // ⚠️ This API answers 200 with a `success: false` body on failure, so
      // axios does not throw and a `catch` alone would treat a rejected upload
      // as a successful one. Every other screen in this module that skipped
      // this check has been a bug — see §3.7 of the handoff doc.
      const body = res?.data;
      if (!body?.success || !body?.data?.url) {
        setError(body?.statusMessage || "The upload failed. Please try again.");
        return;
      }
      onChange({ url: body.data.url, name: body.data.name });
    } catch (err) {
      setError(
        err.response?.data?.statusMessage ||
          err.message ||
          "The upload failed. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography
        component="label"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#374151",
          fontFamily: fontFam,
          mb: 0.5,
          display: "block",
        }}
      >
        {label}
      </Typography>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleFile}
        style={{ display: "none" }}
      />

      {attached ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            border: `1px solid ${TEAL[100]}`,
            bgcolor: TEAL[50],
            borderRadius: "8px",
            px: 1.5,
            py: 1.1,
          }}
        >
          <PictureAsPdfOutlinedIcon sx={{ fontSize: 20, color: TEAL[600], flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: fontFam,
                fontSize: 13,
                fontWeight: 600,
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={value.name || "Job description"}
            >
              {value.name || "Job description"}
            </Typography>
            <Typography
              sx={{ fontFamily: fontFam, fontSize: 11, color: TEAL[700] }}
            >
              {/* Said plainly, because it is the point of the feature and not
                  obvious from a filename sitting in a form. */}
              Attached to interview invitations sent for this posting
            </Typography>
          </Box>

          <Tooltip title="Open the PDF">
            <IconButton
              component="a"
              href={value.url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ color: TEAL[600] }}
            >
              <OpenInNewIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>

          <Button
            onClick={pick}
            disabled={disabled || uploading}
            size="small"
            sx={{
              textTransform: "none",
              fontFamily: fontFam,
              fontSize: 12,
              fontWeight: 600,
              color: TEAL[700],
              minWidth: 0,
              "&:hover": { bgcolor: TEAL[100] },
            }}
          >
            {uploading ? <CircularProgress size={14} sx={{ color: TEAL[600] }} /> : "Replace"}
          </Button>

          <Tooltip title="Remove the PDF">
            <IconButton
              onClick={() => {
                setError("");
                // Null, not {url:""} — the parent sends null through to the
                // column, and an empty string would store a value that reads
                // as "there is a document" to anything checking truthiness in
                // SQL later.
                onChange(null);
              }}
              disabled={disabled || uploading}
              size="small"
              sx={{ color: "#b91c1c" }}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Button
          onClick={pick}
          disabled={disabled || uploading}
          startIcon={
            uploading ? (
              <CircularProgress size={15} sx={{ color: TEAL[600] }} />
            ) : (
              <UploadFileOutlinedIcon sx={{ fontSize: 18 }} />
            )
          }
          fullWidth
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            fontFamily: fontFam,
            fontSize: 13,
            fontWeight: 600,
            color: TEAL[700],
            border: "1px dashed #cbd5e1",
            borderRadius: "8px",
            px: 1.5,
            py: 1.1,
            "&:hover": { borderColor: TEAL[300], bgcolor: TEAL[50] },
          }}
        >
          {uploading ? "Uploading…" : "Attach a PDF"}
        </Button>
      )}

      {inheritedNote && attached && (
        <Typography
          sx={{ fontFamily: fontFam, fontSize: 11, color: "#64748b", mt: 0.6 }}
        >
          {inheritedNote}
        </Typography>
      )}

      {!attached && helperText && !error && (
        <Typography
          sx={{ fontFamily: fontFam, fontSize: 11, color: "#94a3b8", mt: 0.6 }}
        >
          {helperText}
        </Typography>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mt: 1, borderRadius: "8px", fontSize: 12, fontFamily: fontFam }}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default JdAttachmentField;
